import * as THREE from 'three'
import type { WthitInspector } from './wthit-inspector'
import type { SoundEffects } from '../audio/sfx'
import { createDebugToast } from './_debug-helpers'

export interface WthitCopiedPayload {
  id: string
  name: string
  type: string
  sourceTag?: string | null
  distanceMeters: number
  position: { x: number; y: number; z: number }
  dimensionsMeters?: { width: number; height: number; depth: number } | null
  details?: Record<string, any>
  gltfAsset?: string | null
  timestamp: string
}

const showCopyToast = createDebugToast({
  id: 'wthit-copy-toast',
  topPx: 78,
  successColor: 'rgba(0, 240, 255, 0.95)',
  successShadowRgba: 'rgba(0, 240, 255, 0.4)',
})

export function copyObjectDataAsJson(
  inspector: WthitInspector,
  camera?: THREE.Camera,
  scene?: THREE.Scene,
  sfx?: SoundEffects,
): WthitCopiedPayload | null {
  const data = inspector.getCurrentTargetData(camera)

  if (!data) {
    showCopyToast('⚠️ NO TARGETED OBJECT IN CROSSHAIRS', false)
    return null
  }

  const jsonStr = JSON.stringify(data, null, 2)

  // Copy to system clipboard
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(jsonStr).catch(() => {
      // Fallback for non-secure contexts
      try {
        const textarea = document.createElement('textarea')
        textarea.value = jsonStr
        document.body.appendChild(textarea)
        textarea.select()
        document.execCommand('copy')
        document.body.removeChild(textarea)
      } catch {}
    })
  }

  // Set debug global references for inspection/automation
  if (typeof window !== 'undefined') {
    ;(window as any).__lastWthitCopy = jsonStr
    ;(window as any).__lastWthitData = data
  }

  console.log(`%c[WTHIT-COPY] JSON Copied for ${data.name}:`, 'color: #00f0ff; font-weight: bold;', data)

  // Scanner Visual Beam Effect
  if (scene && camera) {
    const beamGeom = new THREE.CylinderGeometry(0.02, 0.02, data.distanceMeters, 8)
    beamGeom.rotateX(Math.PI / 2)
    const beamMat = new THREE.MeshBasicMaterial({ color: 0x00f0ff, transparent: true, opacity: 0.9 })
    const beamMesh = new THREE.Mesh(beamGeom, beamMat)

    const camPos = camera.position.clone()
    const targetPos = new THREE.Vector3(data.position.x, data.position.y, data.position.z)
    const midPoint = camPos.clone().lerp(targetPos, 0.5)

    beamMesh.position.copy(midPoint)
    beamMesh.lookAt(targetPos)
    scene.add(beamMesh)

    // Fade beam out quickly
    if (typeof requestAnimationFrame !== 'undefined') {
      const startTime = performance.now()
      const animateBeam = () => {
        const elapsed = performance.now() - startTime
        if (elapsed > 180) {
          scene.remove(beamMesh)
          beamGeom.dispose()
          beamMat.dispose()
        } else {
          beamMat.opacity = (1 - elapsed / 180) * 0.9
          requestAnimationFrame(animateBeam)
        }
      }
      requestAnimationFrame(animateBeam)
    } else {
      scene.remove(beamMesh)
      beamGeom.dispose()
      beamMat.dispose()
    }
  }

  // Audio SFX trigger
  try {
    sfx?.playScannerChirp()
  } catch {}

  const dimStr = data.dimensionsMeters
    ? ` (${data.dimensionsMeters.width}×${data.dimensionsMeters.height}×${data.dimensionsMeters.depth}m)`
    : ''
  showCopyToast(`📋 COPIED JSON: ${data.name}${dimStr}`, true)

  return data as WthitCopiedPayload
}
